<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import SatisfacaoAPI from 'dashboard/api/fandata/satisfacao';
import DeltaPeriodo from './DeltaPeriodo.vue';
import AbasRelatorio from './components/AbasRelatorio.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import CsatResponses from './CsatResponses.vue';

// Satisfação (M3): correlação CSAT × causa raiz da triagem.
//
// T-150h: "satisfação e CSAT numa página só, com download", palavra dela. Eram duas telas
// medindo a mesma coisa: esta responde ONDE a nota cai (por causa raiz), a nativa lista as
// respostas uma a uma e exporta. Viraram duas abas, e o download continua onde a pessoa espera.
const { t } = useI18n();
const route = useRoute();

const ABA_PANORAMA = 'panorama';
const ABA_RESPOSTAS = 'respostas';
// A rota antiga /reports/csat redireciona para cá com ?aba=respostas.
const abaAtiva = ref(
  route.query.aba === ABA_RESPOSTAS ? ABA_RESPOSTAS : ABA_PANORAMA
);
const abas = computed(() => [
  { key: ABA_PANORAMA, label: t('SATISFACAO.ABAS.PANORAMA') },
  { key: ABA_RESPOSTAS, label: t('SATISFACAO.ABAS.RESPOSTAS') },
]);

// T-256: o recorte por lado. 'todos' não vai para a API: ausente já significa a operação
// inteira, e mandar a palavra obrigaria o construtor a conhecer um terceiro valor.
const LADO_TODOS = 'todos';
const lado = ref(LADO_TODOS);
// T-252: o canal entra junto do lado, no mesmo controle usado pelas outras telas de relatório.
const canalFiltrado = ref(null);

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));

const data = reactive({
  // T-210: a curva do número global, dia a dia.
  serie: [],
  counters: {},
  por_assunto: [],
  por_criterio: {},
  // T-257 e T-279.
  por_lado: {},
  avaliacao_bira: {},
});

// T-174: as notas separadas por critério. Somar "qualidade do atendimento" e "resolução do
// problema" numa média só apaga o que o clube quer enxergar.
const criterios = computed(() =>
  Object.entries(data.por_criterio || {}).map(([chave, v]) => ({ chave, ...v }))
);

// R3.11: comparação com o período anterior. O volume de respostas entra porque CSAT alto com
// três respostas não é a mesma notícia que com trezentas, e sem esse número ao lado a queda de
// participação passa despercebida.
// T-257. Só aparece quando os DOIS lados têm resposta: com um lado zerado a frase diria
// "Bira 45%, humano 0%", e 0% ali significa "ninguém respondeu", não "nota zero".
// T-256: com um lado escolhido a frase sai de cena, porque o número grande JÁ é aquele lado e
// repeti-lo embaixo faria parecer que são duas medidas diferentes.
const composicaoDoCsat = computed(() => {
  if (lado.value !== LADO_TODOS) return null;
  const bira = data.por_lado?.bira;
  const humano = data.por_lado?.humano;
  if (!bira?.respostas || !humano?.respostas) return null;
  return t('SATISFACAO.COMPOSICAO', { bira: bira.csat, humano: humano.csat });
});

// T-256 (02/10): a Tamara pediu o subtítulo "em cada big number da aba Todos". Nos três
// contadores de CONTAGEM a leitura é a PARTICIPAÇÃO de cada lado no total, e não a nota de cada
// um: aqui "Bira 60%" quer dizer "60% dessas respostas vieram de conversas do Bira".
//
// ⚠️ O CSAT é diferente de propósito, e continua como estava: lá os dois números são o CSAT de
// cada lado, porque participação numa MÉDIA não quer dizer nada. Os dois subtítulos se parecem
// na tela, e é a diferença entre "quanto é de cada um" e "quanto cada um tirou".
const participacao = campo => {
  if (lado.value !== LADO_TODOS) return null;
  const bira = data.por_lado?.bira?.[campo] ?? 0;
  const humano = data.por_lado?.humano?.[campo] ?? 0;
  const total = bira + humano;
  if (!total) return null;
  return t('SATISFACAO.COMPOSICAO', {
    bira: Math.round((bira * 100) / total),
    humano: Math.round((humano * 100) / total),
  });
};

// T-256. As duas linhas da comparação, sempre as duas, independentes do seletor: é este bloco
// que responde "qual dos dois está puxando para baixo", e com uma linha só não há comparação.
// Linha sem resposta aparece com um travessão em vez de 0%, porque 0% ali significaria nota
// zero, e o que houve foi ninguém ter respondido.
const ladoLinhas = computed(() => {
  const lados = ['bira', 'humano'].map(chave => {
    const v = data.por_lado?.[chave] || {};
    return {
      chave,
      label: t(`SATISFACAO.LADOS.${chave.toUpperCase()}`),
      respostas: v.respostas || 0,
      csat: v.respostas ? v.csat : null,
      media: v.respostas ? v.media : null,
      insatisfeitos: v.insatisfeitos || 0,
    };
  });

  // QUANTO CADA LADO REPRESENTA (pedido dela em 08/10: "não foi incluído o % do Bira e %
  // humano"). O bloco mostrava só a contagem, e contagem sozinha não diz se a nota do Bira pesa
  // na média geral: 40% de insatisfeitos em 5 respostas é outra conversa que em 500.
  //
  // O segundo lado é 100 menos o primeiro, em vez de arredondado por conta própria: dois
  // arredondamentos independentes somam 99 ou 101 na tela, e ninguém consegue explicar isso para
  // quem está lendo o relatório.
  const total = lados.reduce((soma, lado) => soma + lado.respostas, 0);
  if (!total) return lados.map(lado => ({ ...lado, fatia: null }));

  const primeira = Math.round((lados[0].respostas * 100) / total);
  return lados.map((lado, i) => ({ ...lado, fatia: i === 0 ? primeira : 100 - primeira }));
});

// T-279. A pergunta do T-211 é sim/não, não é nota: ela não entra na média de satisfação,
// porque somar duas perguntas diferentes num número só é o erro que o T-210 já evita.
const avaliacaoBira = computed(() => {
  const a = data.avaliacao_bira || {};
  if (!a.responderam) return null;
  return {
    acerto: a.acerto,
    sim: a.sim,
    nao: a.nao,
    responderam: a.responderam,
    perguntados: a.perguntados,
  };
});

// T-210 · A CURVA DA SATISFAÇÃO TOTAL. O cartão responde "como estamos no período"; esta linha
// responde "está melhorando ou piorando", que é outra pergunta e não se responde comparando dois
// totais de cabeça.
//
// Dia sem resposta INTERROMPE a linha em vez de cair a zero: zero aqui se leria como "todo mundo
// insatisfeito", quando o que houve é que ninguém respondeu naquele dia.
const GRAF = { largura: 720, altura: 140, pad: 22 };

const tendencia = computed(() => {
  const serie = data.serie || [];
  const medidos = serie.filter(d => d.csat !== null && d.csat !== undefined);
  if (serie.length < 2 || medidos.length < 2) return null;

  const passo = (GRAF.largura - GRAF.pad * 2) / (serie.length - 1);
  const y = v => GRAF.altura - GRAF.pad - (v / 100) * (GRAF.altura - GRAF.pad * 2);

  let caminho = '';
  let caneta = false;
  serie.forEach((d, i) => {
    if (d.csat === null || d.csat === undefined) {
      caneta = false;
      return;
    }
    caminho += `${caneta ? 'L' : 'M'} ${GRAF.pad + i * passo} ${y(d.csat)} `;
    caneta = true;
  });

  const diaCurto = iso => iso.slice(8, 10) + '/' + iso.slice(5, 7);
  return {
    caminho,
    primeiroDia: diaCurto(serie[0].dia),
    ultimoDia: diaCurto(serie[serie.length - 1].dia),
    diasMedidos: medidos.length,
    respostas: medidos.reduce((soma, d) => soma + d.respostas, 0),
  };
});

const counters = computed(() => {
  const d = data.counters.delta || {};
  return [
    {
      key: 'RESPOSTAS',
      value: data.counters.respostas ?? 0,
      accent: 'text-n-slate-12',
      delta: d.respostas,
      sufixo: '%',
      composicao: participacao('respostas'),
    },
    {
      key: 'CSAT',
      value: `${data.counters.csat ?? 0}%`,
      accent: 'text-n-teal-11',
      delta: d.csat,
      sufixo: 'pts',
      // T-257: "se o meu CSAT geral é 45, preciso saber qual dos dois está puxando para
      // baixo". A média sozinha esconde exatamente isso.
      composicao: composicaoDoCsat.value,
    },
    {
      key: 'SATISFEITOS',
      value: data.counters.satisfeitos ?? 0,
      accent: 'text-n-teal-11',
      composicao: participacao('satisfeitos'),
    },
    {
      key: 'INSATISFEITOS',
      value: data.counters.insatisfeitos ?? 0,
      accent: 'text-n-ruby-11',
      composicao: participacao('insatisfeitos'),
    },
  ];
});

const fetchData = async () => {
  try {
    const { data: res } = await SatisfacaoAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      lado: lado.value === LADO_TODOS ? undefined : lado.value,
      inboxId: canalFiltrado.value,
    });
    data.counters = res.counters || {};
    data.por_assunto = res.por_assunto || [];
    data.por_criterio = res.por_criterio || {};
    data.por_lado = res.por_lado || {};
    data.avaliacao_bira = res.avaliacao_bira || {};
    data.serie = res.serie || [];
  } catch (e) {
    useAlert(t('SATISFACAO.ERROR'));
  }
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
};

const onLadoChange = valor => {
  lado.value = valor;
  fetchData();
};

const onCanalChange = valor => {
  canalFiltrado.value = valor;
  fetchData();
};

const csatStyle = csat => {
  if (csat >= 80) return 'text-n-teal-11';
  if (csat >= 50) return 'text-n-amber-11';
  return 'text-n-ruby-11';
};

// ⚠️ O CLIQUE NA LINHA SAIU em 05/10, junto com a troca do agrupamento para causa raiz.
//
// Ele levava para a lista de conversas filtrada pela ETIQUETA, e só funcionava porque a linha ERA
// uma etiqueta: a categoria tem etiqueta, a causa raiz não (são 203 valores, que não cabem no
// painel de etiquetas · ver Fandata::CausaRaiz). Manter o clique apontando para uma etiqueta que
// não existe abriria uma lista vazia, que é pior que não ter clique.
//
// Quem precisa ver as conversas de uma causa raiz tem caminho: o Farol, na aba Cruzamento, abre a
// cortina com elas ao clicar na linha. Trazer essa cortina para cá é item próprio, e não entrou
// aqui para a troca do agrupamento não vir embrulhada com tela nova.

const { startRefetching } = useLiveRefresh(fetchData, 60000);
onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <!-- Header -->
    <div class="flex flex-col gap-3 mb-4">
      <div class="flex items-center gap-2">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('SATISFACAO.HEADER') }}
        </h1>
      </div>
      <p class="text-sm text-n-slate-11 -mt-1">{{ t('SATISFACAO.HINT') }}</p>
    </div>

    <AbasRelatorio v-model="abaAtiva" :abas="abas" />

    <CsatResponses v-if="abaAtiva === 'respostas'" embutido />

    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <BaixarRelatorio
          nome="satisfacao"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
      <!-- T-256 (02/10): o atalho saiu por pedido da Tamara, "pois leva para a página de tempo de
         atendimento", que mede outra coisa. -->
        <!-- T-256: o recorte vale para os contadores, para a quebra por critério e para a
             tabela por causa raiz. O bloco "quem atendeu" abaixo continua mostrando os dois
             lados, porque ele É a comparação. -->
        <FiltrosRelatorio
          :inbox-id="canalFiltrado"
          :lado="lado"
          @update:inbox-id="onCanalChange"
          @update:lado="onLadoChange"
        />
      </div>

      <p v-if="lado !== 'todos'" class="mb-4 text-xs text-n-slate-11">
        {{ t(`SATISFACAO.LADOS.AVISO_${lado.toUpperCase()}`) }}
      </p>

      <!-- Contadores -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div
          v-for="c in counters"
          :key="c.key"
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t(`SATISFACAO.COUNTERS.${c.key}`) }}
          </span>
          <div class="flex flex-wrap gap-2 items-baseline">
            <span class="text-2xl font-semibold" :class="c.accent">
              {{ c.value }}
            </span>
            <DeltaPeriodo
              :valor="c.delta ?? null"
              :sufixo="c.sufixo || 'pts'"
              :melhor-quando="c.melhorQuando || 'sobe'"
            />
          </div>
          <!-- T-257: a média em cima, e embaixo, menor, quanto é Bira e quanto é humano. -->
          <span v-if="c.composicao" class="text-xs text-n-slate-11">
            {{ c.composicao }}
          </span>
        </div>
      </div>

      <!-- T-210: a satisfação total ao longo do tempo. O cartão acima é o total do período;
           esta linha diz se ele está subindo ou caindo. A ressalva fica junto do gráfico, e não
           escondida num rodapé, porque é ela que impede o número de ser levado a uma reunião
           como se fosse a satisfação por atendimento. -->
      <div
        v-if="tendencia"
        class="flex flex-col gap-2 p-5 mb-6 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('SATISFACAO.TENDENCIA.TITULO') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{
              t('SATISFACAO.TENDENCIA.HINT', {
                dias: tendencia.diasMedidos,
                respostas: tendencia.respostas,
              })
            }}
          </p>
        </div>
        <div class="overflow-x-auto">
          <svg viewBox="0 0 720 140" class="w-full min-w-[420px] h-[140px]">
            <path
              :d="tendencia.caminho"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-n-teal-11"
            />
          </svg>
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>{{ tendencia.primeiroDia }}</span>
          <span>{{ t('SATISFACAO.TENDENCIA.ESCALA') }}</span>
          <span>{{ tendencia.ultimoDia }}</span>
        </div>
        <p class="text-xs text-n-amber-11">
          {{ t('SATISFACAO.TENDENCIA.RESSALVA') }}
        </p>
      </div>

      <!-- T-279: a resposta do torcedor sobre a PRECISÃO do Bira (T-211). Cartão separado, e
           não mais uma caixinha na fileira de cima, porque é outra pergunta: aquelas medem
           satisfação de 1 a 5, esta é sim ou não. Misturar as duas num número só é o erro que
           o T-210 já evita. Só aparece quando alguém respondeu. -->
      <div
        v-if="avaliacaoBira"
        class="flex flex-col gap-1 p-4 mb-6 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('SATISFACAO.AVALIACAO_BIRA.TITULO') }}
        </span>
        <div class="flex flex-wrap items-baseline gap-2">
          <span class="text-2xl font-semibold text-n-teal-11">
            {{ avaliacaoBira.acerto }}%
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              t('SATISFACAO.AVALIACAO_BIRA.DETALHE', {
                sim: avaliacaoBira.sim,
                nao: avaliacaoBira.nao,
                responderam: avaliacaoBira.responderam,
                perguntados: avaliacaoBira.perguntados,
              })
            }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ t('SATISFACAO.AVALIACAO_BIRA.HINT') }}
        </span>
      </div>

      <!-- T-256/T-257: quem atendeu. Fica FORA do seletor de propósito: é a comparação entre os
           dois lados, e filtrá-la deixaria a outra linha zerada. É também a leitura que ela
           pediu: "se o meu CSAT geral é 45, preciso saber qual dos dois". -->
      <div class="mb-6 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('SATISFACAO.BY_SIDE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('SATISFACAO.BY_SIDE_HINT') }}
          </p>
        </div>
        <div class="grid gap-px bg-n-weak sm:grid-cols-2">
          <button
            v-for="linha in ladoLinhas"
            :key="linha.chave"
            type="button"
            class="px-5 py-4 text-left transition bg-n-solid-1 hover:bg-n-alpha-1"
            @click="onLadoChange(lado === linha.chave ? 'todos' : linha.chave)"
          >
            <p class="text-xs font-medium text-n-slate-11">
              {{ linha.label }}
              <span
                v-if="linha.fatia !== null"
                class="font-normal tabular-nums text-n-slate-10"
              >
                · {{ t('SATISFACAO.BY_SIDE_FATIA', { fatia: linha.fatia }) }}
              </span>
            </p>
            <p class="flex items-baseline gap-2 mt-1">
              <span
                class="text-2xl font-semibold tabular-nums"
                :class="linha.csat === null ? 'text-n-slate-10' : csatStyle(linha.csat)"
              >
                {{ linha.csat === null ? '–' : `${linha.csat}%` }}
              </span>
              <span v-if="linha.media !== null" class="text-sm tabular-nums text-n-slate-11">
                {{ linha.media }}
              </span>
            </p>
            <p class="text-xs tabular-nums text-n-slate-11">
              {{
                t('SATISFACAO.BY_SIDE_DETALHE', {
                  respostas: linha.respostas,
                  insatisfeitos: linha.insatisfeitos,
                })
              }}
            </p>
          </button>
        </div>
      </div>

      <!-- Por assunto -->
      <!-- T-174: a quebra por critério. Fica ANTES do por assunto porque responde a pergunta mais
           básica: a nota que está ali em cima é de quê. -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden mb-6"
      >
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('SATISFACAO.BY_CRITERION') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('SATISFACAO.BY_CRITERION_HINT') }}
          </p>
        </div>
        <div v-if="!criterios.length" class="px-5 py-8">
          <p class="text-sm text-n-slate-11">
            {{ t('SATISFACAO.BY_CRITERION_EMPTY') }}
          </p>
        </div>
        <div v-else class="grid gap-px bg-n-weak sm:grid-cols-3">
          <div
            v-for="c in criterios"
            :key="c.chave"
            class="bg-n-solid-1 px-5 py-4"
          >
            <p class="text-xs font-medium text-n-slate-11">{{ c.rotulo }}</p>
            <p class="mt-1 flex items-baseline gap-2">
              <span
                class="text-2xl font-semibold tabular-nums"
                :class="csatStyle(c.csat)"
              >
                {{ c.csat }}%
              </span>
              <span class="text-sm text-n-slate-11 tabular-nums">
                {{ c.media }}
              </span>
            </p>
            <p class="text-xs text-n-slate-11 tabular-nums">
              {{ c.respostas }} {{ t('SATISFACAO.CRITERION_ANSWERS') }}
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('SATISFACAO.BY_SUBJECT') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('SATISFACAO.BY_SUBJECT_HINT') }}
          </p>
        </div>

        <div v-if="!data.por_assunto.length" class="px-5 py-10 text-center">
          <p class="text-sm text-n-slate-11">{{ t('SATISFACAO.EMPTY') }}</p>
        </div>

        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-n-slate-11">
              <th class="text-left font-medium px-5 py-2">
                {{ t('SATISFACAO.ASSUNTO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('SATISFACAO.COL_RESPOSTAS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('SATISFACAO.COL_SATISFEITOS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('SATISFACAO.COL_NEUTROS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('SATISFACAO.COL_INSATISFEITOS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('SATISFACAO.COL_MEDIA') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.por_assunto"
              :key="row.assunto"
              class="border-t border-n-weak hover:bg-n-alpha-1"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12 capitalize">
                {{ row.label }}
              </td>
              <td class="px-3 py-2 text-center text-n-slate-12">
                {{ row.respostas }}
              </td>
              <!-- T-256: distribuição em PORCENTAGEM, e não em contagem. "8 insatisfeitos"
                   não se compara entre uma causa com 10 respostas e outra com 300. -->
              <td
                class="px-3 py-2 text-center font-semibold"
                :class="csatStyle(row.csat)"
              >
                {{ row.csat }}%
              </td>
              <td class="px-3 py-2 text-center text-n-amber-11">
                {{ row.pct_neutros }}%
              </td>
              <td class="px-3 py-2 text-center text-n-ruby-11">
                {{ row.pct_insatisfeitos }}%
              </td>
              <td class="px-3 py-2 text-center text-n-slate-12">
                {{ row.media }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
